import { v4 } from "uuid";
import type { BisacCode } from "reedy-data/api";
import { FirestoreProductBisacSubject, SubjectSource } from "reedy-data/models";
import { formatBisacHeading } from "./formatBisacHeading";

/** Matches the backend's `normalize_bisac_code`; the stored code stays as imported. */
export const normalizeBisacCode = (code: string | null | undefined) =>
  (code ?? "").trim().toUpperCase();

export const getDatabaseBisacSubjects = (
  subjects: FirestoreProductBisacSubject[],
) => subjects.filter((subject) => subject.inSource && subject.subjectCode);

export const getFinalBisacSubjects = (
  subjects: FirestoreProductBisacSubject[],
) =>
  subjects
    .filter((subject) => subject.final)
    .sort(
      (a, b) =>
        (a.orderIndex ?? Number.MAX_SAFE_INTEGER) -
        (b.orderIndex ?? Number.MAX_SAFE_INTEGER),
    );

/**
 * Writes the final selection as `final` + `orderIndex`, and drops entries that
 * are neither in the ONIX file nor selected, as the import does.
 */
const withFinalOrder = (
  subjects: FirestoreProductBisacSubject[],
  finalIds: string[],
) =>
  subjects
    .map((subject) => {
      const orderIndex = finalIds.indexOf(subject.id);
      return orderIndex === -1
        ? { ...subject, final: false, orderIndex: null }
        : { ...subject, final: true, orderIndex };
    })
    .filter((subject) => subject.inSource || subject.final);

const finalIdsOf = (subjects: FirestoreProductBisacSubject[]) =>
  getFinalBisacSubjects(subjects).map(({ id }) => id);

export const toManualSubject = (
  code: BisacCode,
): FirestoreProductBisacSubject => ({
  id: v4(),
  subjectCode: code.code,
  subjectHeadingText: formatBisacHeading(code),
  source: SubjectSource.MANUAL,
  inSource: false,
  final: true,
  orderIndex: null,
  seq: code.seq,
});

/** Appends codes to the final list; a code already on the product is reused. */
export const addFinalBisacCodes = (
  subjects: FirestoreProductBisacSubject[],
  codes: BisacCode[],
  newSubjectFor = toManualSubject,
) => {
  const all = [...subjects];
  const finalIds = finalIdsOf(subjects);

  for (const code of codes) {
    const normalizedCode = normalizeBisacCode(code.code);
    let subject = all.find(
      ({ subjectCode }) => normalizeBisacCode(subjectCode) === normalizedCode,
    );
    if (!subject) {
      subject = newSubjectFor(code);
      all.push(subject);
    }
    if (!finalIds.includes(subject.id)) finalIds.push(subject.id);
  }

  return withFinalOrder(all, finalIds);
};

export const addFinalBisacSubject = (
  subjects: FirestoreProductBisacSubject[],
  id: string,
) => {
  const finalIds = finalIdsOf(subjects);
  return withFinalOrder(
    subjects,
    finalIds.includes(id) ? finalIds : [...finalIds, id],
  );
};

export const removeFinalBisacSubject = (
  subjects: FirestoreProductBisacSubject[],
  id: string,
) =>
  withFinalOrder(
    subjects,
    finalIdsOf(subjects).filter((finalId) => finalId !== id),
  );

/** Ignores an order that no longer matches the stored selection. */
export const reorderFinalBisacSubjects = (
  subjects: FirestoreProductBisacSubject[],
  orderedIds: string[],
) => {
  const finalIds = finalIdsOf(subjects);
  const orderedFinalIds = orderedIds.filter((id) => finalIds.includes(id));
  const isSameSelection = orderedFinalIds.length === finalIds.length;

  return isSameSelection ? withFinalOrder(subjects, orderedFinalIds) : subjects;
};
